/**
 * "Did this key actually resolve?" — the ONE shared way to ask.
 *
 * i18next is configured with `parseMissingKeyHandler: resolveMissingTranslationKey`
 * (see ./config.ts), so a key the catalog cannot resolve comes back as a
 * HUMANIZED LEAF ("Lp", "3") and NEVER as the key itself. That silently broke
 * every `translated === key` check in the app: the export-format cards probed
 * `features.0..7` and rendered "✓3 ✓4 ✓5 ✓6 ✓7", and the super-admin asset
 * category dropdown rendered "Lp" instead of its seeded catalog name "LP Token".
 *
 * The honest primitive is i18next's own `defaultValue`, NOT a post-hoc string
 * comparison:
 *
 *   - i18next reaches `parseMissingKeyHandler` only for a key that resolved to
 *     nothing, and the handler returns the `defaultValue` when one was supplied
 *     (`resolveMissingTranslationKey` = `defaultValue ?? humanize(key)`). So
 *     `t(key, { defaultValue: X })` yields X for a missing key and the REAL
 *     translation otherwise. No guessing, no post-hoc parsing.
 *
 *   - Comparing the result against `humanizeTranslationKey(key)` (what
 *     shared/utils/api.ts did) is a HEURISTIC that false-positives whenever a
 *     real translation happens to equal the humanized leaf:
 *     `superAdmin.assets.form.categories.gaming` legitimately translates to
 *     "Gaming", which humanizes to "Gaming" too. Generalizing that comparison
 *     would silently DISCARD real translations, so it must not become the
 *     shared rule.
 *
 *   - `i18n.exists(key)` is equally honest but needs the i18n INSTANCE. These
 *     call sites (pure services, presentational props) only ever receive `t`,
 *     so `defaultValue` is the primitive that reaches all of them.
 */

/**
 * The subset of i18next's `t` these helpers need: a key plus the `defaultValue`
 * option. The real `t` satisfies it, and so does any `(key: string) => string`
 * (fewer parameters is assignable in TypeScript).
 */
export type TranslateWithDefaultValue = (
  key: string,
  // Interpolation values ride along with `defaultValue` so a localized sentence
  // can NAME what it is talking about ("«Bitpanda CSV (16 columnas)»") through
  // i18next's own interpolation — never through a second, home-made one.
  options?: { defaultValue: string } & Readonly<Record<string, string>>,
) => string

/**
 * Passed as `defaultValue` when the caller only wants to know whether the key
 * resolves. No catalog string is ever this — it is not user-facing copy.
 */
const UNRESOLVED_TRANSLATION_SENTINEL = '__BAT_I18N_UNRESOLVED_SENTINEL__'

/**
 * The catalog value for `key`, or `undefined` when the catalog has no usable
 * copy for it. An entry that exists but holds an EMPTY string counts as
 * unresolved — blank is never something to render.
 */
export function resolveTranslationOrUndefined(
  translate: TranslateWithDefaultValue,
  key: string,
  interpolationValues?: Readonly<Record<string, string>>,
): string | undefined {
  const translated = translate(key, {
    ...interpolationValues,
    defaultValue: UNRESOLVED_TRANSLATION_SENTINEL,
  })
  if (translated === UNRESOLVED_TRANSLATION_SENTINEL) return undefined
  if (translated.length === 0) return undefined
  return translated
}

/**
 * The catalog value for `key`, or the caller's own fallback.
 *
 * Use this wherever the fallback is REAL product copy the humanizer could never
 * guess — a DB catalog row's `name` ("LP Token"), a raw backend code, a
 * hardcoded English sentence. The fallback wins over the humanized leaf.
 */
export function translateWithFallback(
  translate: TranslateWithDefaultValue,
  key: string,
  fallback: string,
  interpolationValues?: Readonly<Record<string, string>>,
): string {
  return resolveTranslationOrUndefined(translate, key, interpolationValues) ?? fallback
}

/** Whether the catalog holds usable copy for `key` (probe loops, optional UI). */
export function hasResolvedTranslation(translate: TranslateWithDefaultValue, key: string): boolean {
  return resolveTranslationOrUndefined(translate, key) !== undefined
}
